import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CSSProperties, ReactNode } from "react";
import { api } from "../utils/api";

// On-brand replacements for the window.prompt/confirm/alert that the custom-series
// rename + delete used (#282 polish). Matches the CreateSeriesModal idiom: portal,
// overlay click-to-close, focus trap + ESC + scroll-lock + focus restore.

function Dialog({
  label,
  onClose,
  busy,
  children,
}: {
  label: string;
  onClose: () => void;
  busy: boolean;
  children: ReactNode;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prevActive = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.querySelector<HTMLElement>("input, button")?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) { e.preventDefault(); onClose(); return; }
      if (e.key !== "Tab") return;
      const list = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])') || []);
      if (!list.length) return;
      const first = list[0], last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      prevActive?.focus?.();
    };
  }, [onClose, busy]);

  return createPortal(
    <div style={styles.overlay} onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div ref={dialogRef} className="card" style={styles.modal} role="dialog" aria-modal="true" aria-label={label} tabIndex={-1}>
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function RenameSeriesModal({
  seriesId,
  currentName,
  onClose,
  onRenamed,
}: {
  seriesId: string;
  currentName: string;
  onClose: () => void;
  onRenamed: (slug: string, name: string) => void;
}) {
  const [name, setName] = useState(currentName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = name.trim();
  const canSubmit = !!trimmed && trimmed !== currentName && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await api<{ name: string; slug: string }>(`/api/custom-series/${seriesId}`, {
        method: "PATCH",
        body: JSON.stringify({ name: trimmed }),
      });
      onRenamed(updated.slug, updated.name);
    } catch (e) {
      setError((e as Error)?.message || "Rename failed.");
      setBusy(false);
    }
  };

  return (
    <Dialog label="Rename series" onClose={onClose} busy={busy}>
      <div style={styles.head}>
        <span className="eyebrow">Custom series</span>
        <h2 style={styles.title}>Rename series</h2>
      </div>
      <label style={styles.field}>
        <span className="section-label" style={{ margin: 0 }}>Series name</span>
        <input
          className="input"
          value={name}
          maxLength={120}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") void submit(); }}
        />
      </label>
      {error && <div style={styles.error}>{error}</div>}
      <div style={styles.actions}>
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={!canSubmit}>
          {busy ? "Saving…" : "Save name"}
        </button>
      </div>
    </Dialog>
  );
}

export function DeleteSeriesModal({
  seriesId,
  seriesName,
  weekCount,
  onClose,
  onDeleted,
}: {
  seriesId: string;
  seriesName: string;
  weekCount: number;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api(`/api/custom-series/${seriesId}`, { method: "DELETE" });
      onDeleted();
    } catch (e) {
      setError((e as Error)?.message || "Delete failed.");
      setBusy(false);
    }
  };

  return (
    <Dialog label="Delete series" onClose={onClose} busy={busy}>
      <div style={styles.head}>
        <span className="eyebrow">Custom series</span>
        <h2 style={styles.title}>Delete this series?</h2>
        <p style={styles.dek}>
          <strong style={{ color: "var(--text-strong)" }}>{seriesName}</strong> and{" "}
          {weekCount === 0
            ? "its schedule"
            : `all ${weekCount} ${weekCount === 1 ? "round" : "rounds"}`}{" "}
          will be permanently deleted. This cannot be undone.
        </p>
      </div>
      {error && <div style={styles.error}>{error}</div>}
      <div style={styles.actions}>
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="btn btn-danger" onClick={() => void submit()} disabled={busy}>
          {busy ? "Deleting…" : "Delete series"}
        </button>
      </div>
    </Dialog>
  );
}

const styles: Record<string, CSSProperties> = {
  overlay: { position: "fixed", inset: 0, zIndex: 1000, background: "rgba(6,8,12,0.72)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "4.5rem 1rem", overflowY: "auto" },
  modal: { width: "100%", maxWidth: "460px", padding: "1.6rem 1.6rem 1.3rem", display: "flex", flexDirection: "column", gap: "1rem" },
  head: { display: "flex", flexDirection: "column", gap: "0.3rem" },
  title: { fontFamily: "var(--font-display)", fontSize: "1.5rem", fontWeight: 700, margin: 0, color: "var(--text-strong)" },
  dek: { margin: 0, fontSize: "0.9rem", color: "var(--text-muted)", lineHeight: 1.45 },
  field: { display: "flex", flexDirection: "column", gap: "0.4rem" },
  error: { fontSize: "0.875rem", color: "var(--flag-red)", lineHeight: 1.4 },
  actions: { display: "flex", justifyContent: "flex-end", gap: "0.6rem", marginTop: "0.4rem" },
};
